import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
import { MatSelect } from '@angular/material/select';
import { WindowSizeService } from '../../service/window-size.service';
import { TranslateService } from '@ngx-translate/core';

interface Branch {
  id: string;
  branchName: string;
  branchCode: string;
}

@Component({
  selector: 'app-branch-search',
  templateUrl: './branch-search.component.html',
  styleUrls: ['./branch-search.component.scss']
})

export class BranchSearch implements OnInit, AfterViewInit, OnDestroy, OnChanges {

  public branchFilterCtrl: UntypedFormControl = new UntypedFormControl();
  public itemMultiCtrl: UntypedFormControl = new UntypedFormControl();
  public branches: ReplaySubject<Branch[]> = new ReplaySubject<Branch[]>(1);

  @ViewChild('singleSelect', { static: true }) singleSelect: MatSelect;

  protected _onDestroy = new Subject<void>();

  @Input() set data(data: any[]) {
    this._data = data;
    this.branches.next(this.data.slice());

    // When data is set, check for persisted branch selection
    this.initializeSelectedBranch();
  }

  get data(): any[] {
    return this._data;
  }

  private _data: any[];

  @Output() result: EventEmitter<any> = new EventEmitter<any>();
  @Input() outlineLabel: string;
  @Input() branchConfig: any;
  @Input() wideWidth = false;
  @Input() selectedBranch: number | null = null;
  account: any = {};

  constructor(private windowSizeService: WindowSizeService, public _translate: TranslateService,) {
  }

  ngOnInit() {
    this.branchFilterCtrl.valueChanges
      .pipe(
        takeUntil(this._onDestroy),
        debounceTime(300) // Wait for 300ms pause in events
      )
      .subscribe(() => {
        this.filterBranches();
      });

    // Initialize with persisted branch selection on component init
    this.initializeSelectedBranch();
  }

  ngOnChanges(changes: SimpleChanges) {
    // When branchConfig input changes, update the selected branch
    if (changes['branchConfig'] && changes['branchConfig'].currentValue) {
      this.setSelectedBranch(changes['branchConfig'].currentValue);
    }
  }

  ngAfterViewInit() {
    this.setInitialValue();
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  /**
   * Initialize selected branch from localStorage or branchConfig input
   */
  private initializeSelectedBranch() {
    if (!this.data || this.data.length === 0) {
      return;
    }

    let selectedBranch = null;

    // First priority: branchConfig input
    if (this.branchConfig) {
      selectedBranch = this.branchConfig;
    } else {
      // Second priority: localStorage
      const branchConfigLocal = localStorage.getItem('BRANCH_CONFIG');
      if (branchConfigLocal) {
        try {
          const parsedConfig = JSON.parse(branchConfigLocal);
          selectedBranch = this.data.find(branch => branch.id === parsedConfig.id);
        } catch (error) {
          console.warn('Error parsing BRANCH_CONFIG from localStorage:', error);
        }
      }
    }

    // Third priority: selectedBranch input
    if (!selectedBranch && this.selectedBranch) {
      selectedBranch = this.data.find(branch => branch.id === this.selectedBranch);
    }

    if (selectedBranch) {
      this.setSelectedBranch(selectedBranch);
    }
  }

  /**
   * Set the selected branch in the form control
   */
  private setSelectedBranch(branch) {
    if (branch && this.itemMultiCtrl) {
      this.itemMultiCtrl.setValue(branch, { emitEvent: false });
    }
  }

  onChange($event) {
    //  this.result.emit($event.value);
    this.result.emit($event);
    // this.itemMultiCtrl.setValue($event);
    this.itemMultiCtrl.setValue($event.value, { emitEvent: false });

    // Persist the selected branch to localStorage
    if ($event.value) {
      localStorage.setItem('SELECTED_BRANCH_ID', $event.value.id.toString());
    }
  }

  isWebView = (): boolean => this.windowSizeService.isWebView;
  isMobileView = (): boolean => this.windowSizeService.isMobileView;
  isSmallMobileView = (): boolean => this.windowSizeService.isSmallMobileView;

  protected setInitialValue() {
    this.branches
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.singleSelect.compareWith = (a: Branch, b: Branch) => a && b && a.id === b.id;
      });
  }

  protected filterBranches() {
    if (!this.data) {
      return;
    }

    let search = this.branchFilterCtrl.value;
    if (!search) {
      this.branches.next(this.data.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    this.branches.next(
      this.data.filter(branch => branch.branchName.toLowerCase().indexOf(search) > -1)
    );
  }
}
